<template>
    <framework>
        <template #side>
            <side-panel-nav/>
        </template>
        <template #main>
            <main-header title="Certificates, Forms & Reports" icon="fa-file-certificate"/>


            <div class="columns mt-6">
                <div class="column is-8 is-12-tablet">

                    <div class="mb-6 pb-4 content" v-if="certifications.length > 0">
                        <h5 class="has-text-white">Certification:</h5>
                        <b-select v-model="cert" class="mt-2">
                            <option v-if="certifications.length > 1" value="">Choose Certification</option>
                            <option v-if="certifications.length > 1" value="all">All Certifications</option>
                            <option v-for="certification in certifications" :key="certification.id" :value="certification.id">{{ certification.title }}</option>
                        </b-select>
                        <div v-if="certifications.length > 1 && cert === ''" class="mt-3 has-text-white is-size-7">
                            <i class="fas fa-info-circle mr-2"></i>Please select a certification to view your certificates and applications.
                        </div>
                    </div>

                    <!-- WIAA Special Message -->
                    <div v-if="cert === 2" class="mb-6 pb-5">
                        <div class="content">
                            <div class="columns">
                                <div class="column is-narrow">
                                    <i class="fad fa-info-circle has-text-white fa-2x"></i>
                                </div>
                                <div class="column has-text-white">
                                    <div class="mb-5 is-size-4">Washington Interscholastic Activities Association (WIAA)</div>
                                    <div class="mb-6"><span class="has-text-weight-bold">Washington Interscholastic Activities Association (WIAA)</span> certifications are managed by <span class="has-text-weight-bold">FinalForms</span>. To update your certification status in FinalForms, log in to FinalForms and click 'Sync Certifications with my247ed' in your profile. If your certifications are not syncing properly please check your
                                        <router-link to="account" class="has-text-white has-text-underline">integration settings</router-link>
                                        and your WIAA Membership ID. See <a href="https://app.my247ed.com/#/wiaa-help" target="_blank" class="has-text-white has-text-underline">this page</a> for additional information and help.
                                    </div>
                                    <a href="https://wiaa.finalforms.com/athletic_association_staff/find_district" target="_blank">
                                        <b-button type="is-light" outlined>View Certification Status at <span class="has-text-weight-bold">FinalForms</span><i class="far fa-up-right-from-square ml-3"></i></b-button>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="loading" class="has-text-white">
                        <i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Loading certificates...
                    </div>

                    <div v-else-if="cert !== ''">
                        <!-- Completed Certificates -->
                        <h2><i class="fad fa-file-certificate fa-fw mr-3"></i>Completed Certificates</h2>
                        
                        <div v-if="completedCertificates.length === 0">
                            <p class="has-text-white mt-5"><i class="far fa-info-circle fa-fw mr-4 ml-5 pl-3"></i>You currently have no completed certificates.</p>
                        </div>
                        <div v-else>
                            <div v-for="certificate in completedCertificates" :key="`${certificate.type}-${certificate.id}`" class="certificate-card mt-4">
                                <div class="columns is-gapless">
                                    <div class="column is-narrow">
                                        <i class="fad fa-fw fa-certificate fa-2x has-text-success mr-3"></i>
                                    </div>
                                    <div class="column">
                                        <div class="columns is-block-tablet is-flex-widescreen">
                                            <div class="column">
                                                <div class="has-text-weight-bold is-size-5 has-text-dark">{{ certificate.certificationCert && certificate.certificationCert.title ? certificate.certificationCert.title : certificate.application.title }}</div>
                                                <div class="mt-2 has-text-grey-dark" v-if="certificate.certification">
                                                    <strong>Certification:</strong> {{ certificate.certification.title }}
                                                </div>
                                                <div class="mt-3 has-text-grey-dark is-size-7">
                                                    <div><strong>Approved:</strong> {{ new Date(certificate.completedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) }}</div>
                                                    <div v-if="certificate.application.organization">
                                                        <strong>Provider:</strong> {{ certificate.application.organization.title }}
                                                    </div>
                                                    <div v-if="certificate.course">
                                                        <strong>Course:</strong> {{ certificate.course.title }}
                                                    </div>
                                                    <div v-if="certificate.conference">
                                                        <strong>Conference:</strong> {{ certificate.conference.title }}
                                                    </div>
                                                    <div v-if="certificate.totalUnits">
                                                        <strong>{{ certificate.certification && certificate.certification.unit_title ? certificate.certification.unit_title : 'Units' }}:</strong> {{ certificate.totalUnits }}
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="column has-text-right is-narrow">
                                                <b-button
                                                    v-if="certificate.type === 'enrollment' && certificate.course"
                                                    type="is-light"
                                                    size=""
                                                    tag="router-link"
                                                    :to="'/course/' + certificate.enrollmentId"
                                                    class="mb-2"
                                                    expanded
                                                >
                                                    <i class="fas fa-external-link-alt mr-2"></i>View Course
                                                </b-button>
                                                <b-button
                                                    v-if="certificate.type === 'registration' && certificate.conference"
                                                    type="is-light"
                                                    size=""
                                                    tag="router-link"
                                                    :to="'/conference/' + certificate.registrationId"
                                                    class="mb-2"
                                                    expanded
                                                >
                                                    <i class="fas fa-external-link-alt mr-2"></i>View Conference
                                                </b-button>
                                                <b-button
                                                    type="is-light"
                                                    size=""
                                                    @click="viewApplication(certificate)"
                                                    expanded
                                                >
                                                    <i class="fas fa-file-certificate mr-2"></i>View Application
                                                </b-button>
                                            </div>
                                        </div>
                                        
                                        <!-- Awards Section (outside the main columns, like the warning message in Enrollment.vue) -->
                                        <div v-if="certificate.awards && certificate.awards.length > 0">
                                            <div v-for="award in certificate.awards" :key="award.id">
                                                <!-- PDF Awards -->
                                                <template v-if="award.type === 'PDF'">
                                                    <b-message type="is-info" class="mt-3">
                                                        <div class="columns is-gapless is-vcentered">
                                                            <div class="column is-narrow">
                                                                <i class="fas fa-file-pdf fa-fw mr-3 has-text-info fa-lg"></i>
                                                            </div>
                                                            <div class="column">
                                                                <div class="is-size-6 has-text-weight-bold">{{ award.title }}</div>
                                                            </div>
                                                            <div class="column is-narrow">
                                                                <b-button
                                                                    tag="a"
                                                                    target="_blank"
                                                                    type="is-primary"
                                                                    size=""
                                                                    :href="getPDFUrl(certificate, award)"
                                                                >
                                                                    <i class="fas fa-download mr-2"></i>Download
                                                                </b-button>
                                                            </div>
                                                        </div>
                                                    </b-message>
                                                </template>
                                                
                                                <!-- Certificate Awards -->
                                                <template v-else-if="award.type === 'CERT'">
                                                    <b-message type="is-success" class="mt-3">
                                                        <div class="columns is-gapless">
                                                            <div class="column is-narrow">
                                                                <i class="fas fa-certificate fa-fw mr-3 has-text-success fa-lg"></i>
                                                            </div>
                                                            <div class="column">
                                                                <div class="is-size-6 has-text-weight-bold">{{ award.title }}</div>
                                                            </div>
                                                        </div>
                                                    </b-message>
                                                </template>
                                                
                                                <!-- Other Award Types -->
                                                <template v-else>
                                                    <b-message type="is-light" class="mt-3">
                                                        <div class="columns is-gapless">
                                                            <div class="column is-narrow">
                                                                <i class="fas fa-award fa-fw mr-3 has-text-info fa-lg"></i>
                                                            </div>
                                                            <div class="column">
                                                                <div class="is-size-6 has-text-weight-bold">{{ award.title }}</div>
                                                            </div>
                                                        </div>
                                                    </b-message>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- In Progress Applications -->
                        <h2 class="mt-6 pt-4"><i class="fad fa-tasks fa-fw mr-2"></i>In Progress Applications</h2>
                        
                        <div v-if="inProgressCertificates.length === 0">
                            <p class="has-text-white mt-5"><i class="far fa-info-circle fa-fw mr-4 ml-5 pl-3"></i>You currently have no applications in progress.  If you are looking to earn a certification, please look for available applications in a course enrollment or conference registration.</p>
                        </div>
                        <div v-else>
                            <div v-for="certificate in inProgressCertificates" :key="`${certificate.type}-${certificate.id}`" class="certificate-card mt-4">
                                <div class="columns is-gapless">
                                    <div class="column is-narrow">
                                        <i class="fad fa-fw fa-hourglass-half fa-2x has-text-warning mr-3"></i>
                                    </div>
                                    <div class="column">
                                        <div class="columns is-block-tablet is-flex-widescreen">
                                            <div class="column">
                                                <div class="has-text-weight-bold is-size-5 has-text-dark">{{ certificate.certificationCert && certificate.certificationCert.title ? certificate.certificationCert.title : certificate.application.title }}</div>
                                                <div class="mt-2 has-text-grey-dark" v-if="certificate.certification">
                                                    <strong>Certification:</strong> {{ certificate.certification.title }}
                                                </div>
                                                <div class="mt-3 has-text-grey-dark is-size-7">
                                                    <div><strong>Status:</strong> Step {{ certificate.progress || 1 }}</div>
                                                    <div v-if="certificate.application.organization">
                                                        <strong>Provider:</strong> {{ certificate.application.organization.title }}
                                                    </div>
                                                    <div v-if="certificate.course">
                                                        <strong>Course:</strong> {{ certificate.course.title }}
                                                    </div>
                                                    <div v-if="certificate.conference">
                                                        <strong>Conference:</strong> {{ certificate.conference.title }}
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="column has-text-right is-narrow">
                                                <b-button
                                                    type="is-primary"
                                                    size=""
                                                    @click="continueApplication(certificate)"
                                                    class="mb-2"
                                                    expanded
                                                >
                                                    Continue Application <i class="fas fa-arrow-right ml-2"></i>
                                                </b-button>
                                                
                                                <b-button
                                                    v-if="certificate.type === 'enrollment' && certificate.course"
                                                    type="is-light"
                                                    size=""
                                                    tag="router-link"
                                                    :to="'/course/' + certificate.enrollmentId"
                                                    expanded
                                                >
                                                    <i class="fas fa-external-link-alt mr-2"></i>View Course
                                                </b-button>
                                                <b-button
                                                    v-if="certificate.type === 'registration' && certificate.conference"
                                                    type="is-light"
                                                    size=""
                                                    tag="router-link"
                                                    :to="'/conference/' + certificate.registrationId"
                                                    expanded
                                                >
                                                    <i class="fas fa-external-link-alt mr-2"></i>View Conference
                                                </b-button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                    
                    <!-- Bottom padding for better scrolling -->
                    <div style="height: 6rem;"></div>

                </div>
            </div>
        </template>
    </framework>
</template>

<script>
    import Framework from "../../components/framework/Framework";
    import MainHeader from "../../components/framework/MainHeader";
    import SidePanelNav from "../../components/framework/SidePanelNav";
    import {mapGetters} from "vuex";
    import {client as http, apiUrl} from "../../http_client";

    export default {
        components: {Framework, SidePanelNav, MainHeader},
        data: function() {
            return {
                cert: null,
                certificates: [],
                certifications: [],
                loading: true,
                apiUrl
            };
        },
        computed: {
            ...mapGetters(['currentUser']),
            filteredCertificates() {
                if (!this.cert || this.cert === '' || this.cert === 'all') {
                    return this.certificates;
                }
                return this.certificates.filter(certificate => 
                    certificate.certification?.id === this.cert
                );
            },
            completedCertificates() {
                return this.filteredCertificates.filter(cert => cert.status === 'completed');
            },
            inProgressCertificates() {
                return this.filteredCertificates.filter(cert => cert.status === 'in_progress');
            }
        },
        methods: {
            loadCertificates() {
                this.loading = true;
                http.get('app/users/certificates', {force: true}).then(response => {
                    this.certificates = response.data.certificates || [];
                    this.certifications = response.data.certifications || [];
                    
                    console.log('Loaded certificates:', this.certificates);
                    console.log('Loaded certifications:', this.certifications);
                    
                    // Set default certification filter
                    if (this.certifications.length === 1) {
                        // Auto-select the only certification
                        this.cert = this.certifications[0].id;
                    } else if (this.certifications.length > 1) {
                        // Force user to choose when multiple certifications exist
                        this.cert = '';
                    } else {
                        // No certifications available
                        this.cert = 'all';
                    }
                    
                    console.log('Selected cert filter:', this.cert);
                    
                    this.loading = false;
                }).catch(err => {
                    console.error('Failed to load certificates:', err);
                    this.loading = false;
                });
            },
            getPDFUrl(certificate, award) {
                if (certificate.type === 'enrollment') {
                    return `${this.apiUrl}app/enrollments/${certificate.enrollmentId}/applications/${certificate.id}/pdf/${award.key}`;
                } else if (certificate.type === 'registration') {
                    return `${this.apiUrl}app/registrations/${certificate.registrationId}/applications/${certificate.id}/pdf/${award.key}`;
                }
                return '#';
            },
            continueApplication(certificate) {
                if (certificate.type === 'enrollment') {
                    this.$router.push({
                        name: 'course-application',
                        params: {
                            enrollmentId: certificate.enrollmentId,
                            applicationId: certificate.id
                        }
                    });
                } else {
                    this.$router.push({
                        name: 'application',
                        params: {
                            registrationId: certificate.registrationId,
                            applicationId: certificate.id
                        }
                    });
                }
            },
            viewApplication(certificate) {
                if (certificate.type === 'enrollment') {
                    this.$router.push({
                        name: 'course-application',
                        params: {
                            enrollmentId: certificate.enrollmentId,
                            applicationId: certificate.id
                        }
                    });
                } else {
                    this.$router.push({
                        name: 'application',
                        params: {
                            registrationId: certificate.registrationId,
                            applicationId: certificate.id
                        }
                    });
                }
            },
        },
        mounted() {
            this.loadCertificates();
        }
    };
</script>

<style lang="scss" scoped>
    h2 {
        font-size: 1.2rem;
        color: white;
        padding-bottom: 0.5rem;
        margin-bottom: 1rem;
        border-bottom: 1px dashed rgba(255, 255, 255, 0.2);

        i {
            color: white;
        }
    }

    .has-text-underline {
        text-decoration: underline;
    }
    
    .certificate-card {
        background-color: white;
        border-radius: 4px;
        box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);
        padding: 1.5rem;
        margin-bottom: 1rem;
    }
    
    .certificate-divider {
        border: none;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        margin: 1.5rem 0;
        
        &:last-child {
            display: none;
        }
    }
    
</style>
